// components/RichTextEditor.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Bold, Italic, Underline, 
  Heading1, Heading2, Heading3, 
  List, ListOrdered, Quote, 
  Image as ImageIcon, Link as LinkIcon, 
  AlignLeft, AlignCenter, AlignRight, 
  Code, Undo, Redo, Save 
} from 'lucide-react';

const RichTextEditor = ({ initialContent = '', onSave }) => {
  const [content, setContent] = useState(initialContent);
  const editorRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = initialContent;
      editorRef.current.focus();
    }
  }, [initialContent]);

  // Update undo/redo state and button active states
  const updateToolbarState = () => {
    setCanUndo(document.queryCommandEnabled('undo'));
    setCanRedo(document.queryCommandEnabled('redo'));
  };

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleInput = () => {
      setContent(editor.innerHTML);
      updateToolbarState();
    };

    const handleSelection = () => {
      updateToolbarState();
    };

    editor.addEventListener('input', handleInput);
    editor.addEventListener('keyup', handleSelection);
    editor.addEventListener('mouseup', handleSelection);
    document.addEventListener('selectionchange', handleSelection);

    return () => {
      editor.removeEventListener('input', handleInput);
      editor.removeEventListener('keyup', handleSelection);
      editor.removeEventListener('mouseup', handleSelection);
      document.removeEventListener('selectionchange', handleSelection);
    };
  }, []);

  const exec = (command, value = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, value);
    setContent(editorRef.current.innerHTML);
    updateToolbarState();
  };

  // Basic paste cleaning
  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const isActive = (cmd) => document.queryCommandState(cmd);

  const insertLink = () => {
    const url = window.prompt('Enter link URL (https://...)', 'https://');
    if (url) exec('createLink', url);
  };

  const insertImage = () => {
    const url = window.prompt('Enter image URL', 'https://');
    if (url) {
      const imgHtml = `<img src="${url}" alt="Inserted image" class="max-w-full h-auto rounded my-4" />`;
      exec('insertHTML', imgHtml);
    }
  };

  const handleSaveClick = () => {
    if (onSave) onSave(content);
  };

  const ToolbarButton = ({ icon: Icon, onClick, active = false, disabled = false, title }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${
        active ? 'bg-blue-100 text-blue-700' : 'text-gray-700'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      <Icon size={18} />
    </button>
  );

  return (
    <div className="border border-gray-300 rounded-xl overflow-hidden shadow-sm bg-white">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-200 p-2 flex flex-wrap gap-1 items-center">
        <div className="flex gap-1">
          <ToolbarButton icon={Undo} onClick={() => exec('undo')} disabled={!canUndo} title="Undo" />
          <ToolbarButton icon={Redo} onClick={() => exec('redo')} disabled={!canRedo} title="Redo" />
        </div>

        <div className="h-6 w-px bg-gray-300 mx-3" />

        <div className="flex gap-1">
          <ToolbarButton icon={Bold} onClick={() => exec('bold')} active={isActive('bold')} title="Bold" />
          <ToolbarButton icon={Italic} onClick={() => exec('italic')} active={isActive('italic')} title="Italic" />
          <ToolbarButton icon={Underline} onClick={() => exec('underline')} active={isActive('underline')} title="Underline" />
        </div>

        <div className="h-6 w-px bg-gray-300 mx-3" />

        <div className="flex gap-1">
          <ToolbarButton icon={Heading1} onClick={() => exec('formatBlock', '<h1>')} active={isActive('formatBlock') === 'h1'} title="Heading 1" />
          <ToolbarButton icon={Heading2} onClick={() => exec('formatBlock', '<h2>')} active={isActive('formatBlock') === 'h2'} title="Heading 2" />
          <ToolbarButton icon={Heading3} onClick={() => exec('formatBlock', '<h3>')} active={isActive('formatBlock') === 'h3'} title="Heading 3" />
        </div>

        <div className="h-6 w-px bg-gray-300 mx-3" />

        <div className="flex gap-1">
          <ToolbarButton icon={List} onClick={() => exec('insertUnorderedList')} active={isActive('insertUnorderedList')} title="Bullet List" />
          <ToolbarButton icon={ListOrdered} onClick={() => exec('insertOrderedList')} active={isActive('insertOrderedList')} title="Numbered List" />
        </div>

        <div className="h-6 w-px bg-gray-300 mx-3" />

        <div className="flex gap-1">
          <ToolbarButton icon={Quote} onClick={() => exec('formatBlock', '<blockquote>')} active={isActive('formatBlock') === 'blockquote'} title="Quote" />
          <ToolbarButton icon={Code} onClick={() => exec('formatBlock', '<pre>')} title="Code Block" />
        </div>

        <div className="h-6 w-px bg-gray-300 mx-3" />

        <div className="flex gap-1">
          <ToolbarButton icon={AlignLeft} onClick={() => exec('justifyLeft')} title="Align Left" />
          <ToolbarButton icon={AlignCenter} onClick={() => exec('justifyCenter')} title="Align Center" />
          <ToolbarButton icon={AlignRight} onClick={() => exec('justifyRight')} title="Align Right" />
        </div>

        <div className="h-6 w-px bg-gray-300 mx-3" />

        <div className="flex gap-1">
          <ToolbarButton icon={LinkIcon} onClick={insertLink} title="Insert / Edit Link" />
          <ToolbarButton icon={ImageIcon} onClick={insertImage} title="Insert Image" />
        </div>

        <div className="flex-1" />

        <button
          onClick={handleSaveClick}
          className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          <Save size={18} />
          Save
        </button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onPaste={handlePaste}
        className="prose prose-lg max-w-none min-h-[60vh] p-8 focus:outline-none bg-white"
        dangerouslySetInnerHTML={{ __html: content }}
      />

      {/* Status */}
      <div className="bg-gray-50 border-t px-6 py-2.5 text-sm text-gray-600 flex justify-between">
        <div>
          Characters: {content.replace(/<[^>]+>/g, '').length} • Words: {content.replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length}
        </div>
        <div className="text-gray-500 italic">
          Changes not saved
        </div>
      </div>
    </div>
  );
};

export default RichTextEditor;